import { useRef, useState, useCallback, useEffect, useMemo } from 'react';
import {
  StabilizerCode,
  EditorState,
  EditorTool,
  Qubit,
  Stabilizer,
  centroid,
  sortByAngle,
  generateId,
} from '../../types/stabilizer';
import StabilizerInfoPanel from './StabilizerInfoPanel';

const GRID = 40;
const QUBIT_R = 14;
const LOBE_BULGE = 22; // how far the weight-2 lobe bulges outward

function snap(v: number): number {
  return Math.round(v / GRID) * GRID;
}

interface Props {
  code: StabilizerCode;
  editorState: EditorState;
  onCodeChange: (code: StabilizerCode) => void;
  onEditorStateChange: (state: EditorState) => void;
  flippedStabilizerIds?: Set<string>;
}

export default function StabilizerCanvas({
  code,
  editorState,
  onCodeChange,
  onEditorStateChange,
  flippedStabilizerIds,
}: Props) {
  const svgRef = useRef<SVGSVGElement>(null);
  const [dragging, setDragging] = useState<{
    anchorQubitId: string; // qubit under cursor
    startSvg: { x: number; y: number }; // SVG coords at drag start
    qubitStarts: Map<string, { x: number; y: number }>; // original positions of all dragged qubits
  } | null>(null);
  const [hoveredQubitId, setHoveredQubitId] = useState<string | null>(null);
  const [panelPos, setPanelPos] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [viewBox, setViewBox] = useState({ x: -60, y: -60, w: 900, h: 700 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState<{ mx: number; my: number; vx: number; vy: number } | null>(null);
  const [bgDragStart, setBgDragStart] = useState<{ mx: number; my: number } | null>(null);
  // Marquee (rubber-band) selection
  const [marquee, setMarquee] = useState<{ startSvg: { x: number; y: number }; endSvg: { x: number; y: number } } | null>(null);
  const [marqueeBaseSelection, setMarqueeBaseSelection] = useState<Set<string>>(new Set()); // selection before marquee started

  // ── Coordinate conversion ──
  const svgPoint = useCallback(
    (clientX: number, clientY: number) => {
      const svg = svgRef.current;
      if (!svg) return { x: 0, y: 0 };
      const rect = svg.getBoundingClientRect();
      const scaleX = viewBox.w / rect.width;
      const scaleY = viewBox.h / rect.height;
      return {
        x: viewBox.x + (clientX - rect.left) * scaleX,
        y: viewBox.y + (clientY - rect.top) * scaleY,
      };
    },
    [viewBox]
  );

  // ── Zoom ──
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const factor = e.deltaY > 0 ? 1.1 : 0.9;
      const pt = svgPoint(e.clientX, e.clientY);
      setViewBox((vb) => {
        const nw = vb.w * factor;
        const nh = vb.h * factor;
        return {
          x: pt.x - (pt.x - vb.x) * factor,
          y: pt.y - (pt.y - vb.y) * factor,
          w: nw,
          h: nh,
        };
      });
    };
    svg.addEventListener('wheel', onWheel, { passive: false });
    return () => svg.removeEventListener('wheel', onWheel);
  }, [svgPoint]);

  // ── Helpers ──
  const qubitById = useCallback(
    (id: string) => code.qubits.find((q) => q.id === id),
    [code.qubits]
  );

  // Compute shape for a stabilizer: returns { type: 'polygon', points } or { type: 'path', d }
  type StabShape = { type: 'polygon'; points: string } | { type: 'path'; d: string; center: { x: number; y: number } } | null;

  const stabilizerShape = useCallback(
    (stab: Stabilizer): StabShape => {
      const pts = stab.qubitIds
        .map((id) => qubitById(id))
        .filter(Boolean) as Qubit[];
      if (pts.length === 0) return null;
      if (pts.length === 1) {
        const p = pts[0];
        const d = QUBIT_R + 4;
        return { type: 'polygon', points: `${p.x},${p.y - d} ${p.x + d},${p.y} ${p.x},${p.y + d} ${p.x - d},${p.y}` };
      }
      if (pts.length === 2) {
        // Weight-2: arc lobe sharing boundary with neighboring polygon
        const [a, b] = pts;
        const mx = (a.x + b.x) / 2;
        const my = (a.y + b.y) / 2;
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        const len = Math.sqrt(dx * dx + dy * dy) || 1;
        // perpendicular unit vector
        const px = -dy / len;
        const py = dx / len;

        // Find a neighboring stabilizer that contains BOTH qubits of this weight-2 stab
        const neighbor = code.stabilizers.find((s) => {
          if (s.id === stab.id) return false;
          if (s.qubitIds.length < 3) return false; // only match polygons
          return stab.qubitIds.every((qid) => s.qubitIds.includes(qid));
        });

        const bulge = LOBE_BULGE;

        if (neighbor) {
          // Has neighbor: the shared edge is A→B (straight line)
          // Arc bulges AWAY from the neighbor's centroid
          const nPts = neighbor.qubitIds.map((id) => qubitById(id)).filter(Boolean) as Qubit[];
          const nc = centroid(nPts);
          const dot = (nc.x - mx) * px + (nc.y - my) * py;
          const side = dot > 0 ? -1 : 1; // bulge away from neighbor

          const labelX = mx + px * bulge * side * 0.5;
          const labelY = my + py * bulge * side * 0.5;

          // Path: straight line A→B, then arc back B→A on the outer side
          const sweep = side > 0 ? 1 : 0;
          const r = len / 2;
          const d = `M ${a.x},${a.y} L ${b.x},${b.y} A ${r},${bulge} 0 0,${sweep} ${a.x},${a.y} Z`;

          return { type: 'path', d, center: { x: labelX, y: labelY } };
        } else {
          // No neighbor sharing both qubits: use two arcs (lens/eye shape)
          // Decide side based on majority of other centroids
          let side = 1;
          const otherCentroids = code.stabilizers
            .filter((s) => s.id !== stab.id)
            .map((s) => {
              const spts = s.qubitIds.map((id) => qubitById(id)).filter(Boolean) as Qubit[];
              return spts.length > 0 ? centroid(spts) : null;
            })
            .filter(Boolean) as { x: number; y: number }[];
          let posCount = 0, negCount = 0;
          for (const oc of otherCentroids) {
            const dot = (oc.x - mx) * px + (oc.y - my) * py;
            if (dot > 0) posCount++; else negCount++;
          }
          side = posCount <= negCount ? 1 : -1;

          const labelX = mx + px * bulge * side * 0.5;
          const labelY = my + py * bulge * side * 0.5;

          // Two arcs: main arc on the bulge side, smaller arc on the opposite side
          const r = len / 2;
          const sweep = side > 0 ? 1 : 0;
          const d = `M ${a.x},${a.y} A ${r},${bulge} 0 0,${sweep} ${b.x},${b.y} A ${r},${bulge * 0.3} 0 0,${sweep} ${a.x},${a.y} Z`;

          return { type: 'path', d, center: { x: labelX, y: labelY } };
        }
      }
      // Weight >= 3: polygon through qubit positions
      const sorted = sortByAngle(pts);
      return { type: 'polygon', points: sorted.map((p) => `${p.x},${p.y}`).join(' ') };
    },
    [qubitById, code.stabilizers]
  );

  // ── Mouse handlers ──
  const handleSvgMouseDown = (e: React.MouseEvent<SVGSVGElement>) => {
    if (e.button === 1 || (e.button === 0 && e.altKey)) {
      // Middle-click or alt+click: start panning
      setIsPanning(true);
      setPanStart({ mx: e.clientX, my: e.clientY, vx: viewBox.x, vy: viewBox.y });
      e.preventDefault();
      return;
    }

    // Pan tool: left-click anywhere starts panning
    if (e.button === 0 && editorState.tool === 'pan') {
      setIsPanning(true);
      setPanStart({ mx: e.clientX, my: e.clientY, vx: viewBox.x, vy: viewBox.y });
      e.preventDefault();
      return;
    }

    // Only handle left-click on canvas background
    const isBackground = e.target === svgRef.current || (e.target as SVGElement).tagName === 'rect';
    if (!isBackground) return;

    const pt = svgPoint(e.clientX, e.clientY);

    if (editorState.tool === 'addQubit') {
      const sx = snap(pt.x);
      const sy = snap(pt.y);
      // Check no qubit already at this position
      const existing = code.qubits.find(
        (q) => Math.abs(q.x - sx) < 5 && Math.abs(q.y - sy) < 5
      );
      if (!existing) {
        const newQubit: Qubit = {
          id: generateId('q'),
          index: code.nextQubitIndex,
          x: sx,
          y: sy,
        };
        onCodeChange({
          ...code,
          qubits: [...code.qubits, newQubit],
          nextQubitIndex: code.nextQubitIndex + 1,
        });
      }
    } else if (editorState.tool === 'select') {
      // Start marquee selection on left-click drag on background; deselect only on click (no drag)
      const pt = svgPoint(e.clientX, e.clientY);
      setMarquee({ startSvg: pt, endSvg: pt });
      setMarqueeBaseSelection(e.shiftKey ? new Set(editorState.selectedQubitIds) : new Set());
      setBgDragStart({ mx: e.clientX, my: e.clientY });
    }
  };

  const handleSvgMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
    if (isPanning && panStart) {
      const svg = svgRef.current;
      if (!svg) return;
      const rect = svg.getBoundingClientRect();
      const scaleX = viewBox.w / rect.width;
      const scaleY = viewBox.h / rect.height;
      setViewBox((vb) => ({
        ...vb,
        x: panStart.vx - (e.clientX - panStart.mx) * scaleX,
        y: panStart.vy - (e.clientY - panStart.my) * scaleY,
      }));
      return;
    }

    // Marquee selection drag
    if (marquee) {
      const pt = svgPoint(e.clientX, e.clientY);
      setMarquee((m) => m ? { ...m, endSvg: pt } : null);
      // Compute which qubits are inside the marquee rect
      const minX = Math.min(marquee.startSvg.x, pt.x);
      const maxX = Math.max(marquee.startSvg.x, pt.x);
      const minY = Math.min(marquee.startSvg.y, pt.y);
      const maxY = Math.max(marquee.startSvg.y, pt.y);
      const insideIds = new Set(
        code.qubits.filter((q) => q.x >= minX && q.x <= maxX && q.y >= minY && q.y <= maxY).map((q) => q.id)
      );
      // Merge with the selection from before the marquee started
      const base = new Set(marqueeBaseSelection);
      for (const id of insideIds) base.add(id);
      onEditorStateChange({ ...editorState, selectedQubitIds: base, selectedStabilizerId: null });
      return;
    }

    if (dragging) {
      const pt = svgPoint(e.clientX, e.clientY);
      const dx = pt.x - dragging.startSvg.x;
      const dy = pt.y - dragging.startSvg.y;
      // Move all qubits in the drag group
      onCodeChange({
        ...code,
        qubits: code.qubits.map((q) => {
          const start = dragging.qubitStarts.get(q.id);
          if (!start) return q;
          return { ...q, x: start.x + dx, y: start.y + dy };
        }),
      });
    }

    // Update panel position for hovered stabilizer
    const svg = svgRef.current;
    if (svg) {
      const rect = svg.getBoundingClientRect();
      setPanelPos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
    }
  };

  const handleSvgMouseUp = (e: React.MouseEvent<SVGSVGElement>) => {
    // Marquee selection end
    if (marquee) {
      // If it was just a click (no real drag), deselect
      if (bgDragStart) {
        const dx = e.clientX - bgDragStart.mx;
        const dy = e.clientY - bgDragStart.my;
        if (Math.abs(dx) < 4 && Math.abs(dy) < 4 && !e.shiftKey) {
          onEditorStateChange({
            ...editorState,
            selectedQubitIds: new Set(),
            selectedStabilizerId: null,
          });
        }
      }
      setMarquee(null);
      setBgDragStart(null);
    }

    // Snap all dragged qubits to grid on release
    if (dragging) {
      onCodeChange({
        ...code,
        qubits: code.qubits.map((q) => {
          if (!dragging.qubitStarts.has(q.id)) return q;
          return { ...q, x: snap(q.x), y: snap(q.y) };
        }),
      });
      setDragging(null);
    }

    setIsPanning(false);
    setPanStart(null);
  };

  const handleSvgMouseLeave = () => {
    // Snap dragged qubits on leave too
    if (dragging) {
      onCodeChange({
        ...code,
        qubits: code.qubits.map((q) => {
          if (!dragging.qubitStarts.has(q.id)) return q;
          return { ...q, x: snap(q.x), y: snap(q.y) };
        }),
      });
    }
    setMarquee(null);
    setBgDragStart(null);
    setDragging(null);
    setIsPanning(false);
    setPanStart(null);
  };

  const handleQubitMouseDown = (e: React.MouseEvent, qubit: Qubit) => {
    e.stopPropagation();

    if (editorState.tool === 'select') {
      // Determine the set of qubits that will be dragged
      let newSelected: Set<string>;
      if (e.shiftKey) {
        // Shift-click: toggle this qubit in/out of selection
        newSelected = new Set(editorState.selectedQubitIds);
        if (newSelected.has(qubit.id)) {
          newSelected.delete(qubit.id);
        } else {
          newSelected.add(qubit.id);
        }
      } else if (editorState.selectedQubitIds.has(qubit.id)) {
        // Clicking an already-selected qubit: keep entire selection (for group drag)
        newSelected = new Set(editorState.selectedQubitIds);
      } else {
        // Click without shift on unselected qubit: select only this one
        newSelected = new Set([qubit.id]);
      }
      onEditorStateChange({
        ...editorState,
        selectedQubitIds: newSelected,
        selectedStabilizerId: null,
      });

      // Build qubitStarts map for all qubits in the drag group
      const dragIds = newSelected.has(qubit.id) ? newSelected : new Set([qubit.id]);
      const qubitStarts = new Map<string, { x: number; y: number }>();
      for (const q of code.qubits) {
        if (dragIds.has(q.id)) qubitStarts.set(q.id, { x: q.x, y: q.y });
      }
      const pt = svgPoint(e.clientX, e.clientY);
      setDragging({ anchorQubitId: qubit.id, startSvg: pt, qubitStarts });
    } else if (editorState.tool === 'addEdge') {
      if (editorState.edgeStartQubitId === null) {
        onEditorStateChange({ ...editorState, edgeStartQubitId: qubit.id });
      } else if (editorState.edgeStartQubitId !== qubit.id) {
        // Check edge doesn't already exist
        const a = editorState.edgeStartQubitId;
        const b = qubit.id;
        const exists = code.edges.some(
          (e) =>
            (e.qubitIds[0] === a && e.qubitIds[1] === b) ||
            (e.qubitIds[0] === b && e.qubitIds[1] === a)
        );
        if (!exists) {
          onCodeChange({
            ...code,
            edges: [...code.edges, { id: generateId('e'), qubitIds: [a, b] }],
          });
        }
        onEditorStateChange({ ...editorState, edgeStartQubitId: null });
      }
    } else if (editorState.tool === 'addStabilizer') {
      const newSelected = new Set(editorState.selectedQubitIds);
      if (newSelected.has(qubit.id)) {
        newSelected.delete(qubit.id);
      } else {
        newSelected.add(qubit.id);
      }
      onEditorStateChange({ ...editorState, selectedQubitIds: newSelected });
    }
  };

  const handleStabilizerMouseEnter = (stabId: string) => {
    onEditorStateChange({ ...editorState, hoveredStabilizerId: stabId });
  };

  const handleStabilizerMouseLeave = () => {
    onEditorStateChange({ ...editorState, hoveredStabilizerId: null });
  };

  const handleStabilizerClick = (e: React.MouseEvent, stabId: string) => {
    e.stopPropagation();
    if (editorState.tool === 'select') {
      onEditorStateChange({
        ...editorState,
        selectedStabilizerId:
          editorState.selectedStabilizerId === stabId ? null : stabId,
        selectedQubitIds: new Set(),
      });
    }
  };

  const deleteQubit = (qubitId: string) => {
    onCodeChange({
      ...code,
      qubits: code.qubits.filter((q) => q.id !== qubitId),
      edges: code.edges.filter(
        (e) => e.qubitIds[0] !== qubitId && e.qubitIds[1] !== qubitId
      ),
      stabilizers: code.stabilizers
        .map((s) => ({ ...s, qubitIds: s.qubitIds.filter((id) => id !== qubitId) }))
        .filter((s) => s.qubitIds.length > 0),
    });
    setHoveredQubitId(null);
    onEditorStateChange({
      ...editorState,
      selectedQubitIds: new Set([...editorState.selectedQubitIds].filter((id) => id !== qubitId)),
    });
  };

  const deleteStabilizer = (stabId: string) => {
    onCodeChange({
      ...code,
      stabilizers: code.stabilizers.filter((s) => s.id !== stabId),
    });
    onEditorStateChange({
      ...editorState,
      hoveredStabilizerId: null,
      selectedStabilizerId: editorState.selectedStabilizerId === stabId ? null : editorState.selectedStabilizerId,
    });
  };

  // ── Key handler for delete ──
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Delete' || e.key === 'Backspace') {
        // Don't delete if user is typing in an input
        if ((e.target as HTMLElement).tagName === 'INPUT' || (e.target as HTMLElement).tagName === 'TEXTAREA') return;

        if (editorState.selectedStabilizerId) {
          onCodeChange({
            ...code,
            stabilizers: code.stabilizers.filter(
              (s) => s.id !== editorState.selectedStabilizerId
            ),
          });
          onEditorStateChange({ ...editorState, selectedStabilizerId: null });
        } else if (editorState.selectedQubitIds.size > 0) {
          const ids = editorState.selectedQubitIds;
          onCodeChange({
            ...code,
            qubits: code.qubits.filter((q) => !ids.has(q.id)),
            edges: code.edges.filter(
              (e) => !ids.has(e.qubitIds[0]) && !ids.has(e.qubitIds[1])
            ),
            stabilizers: code.stabilizers.map((s) => ({
              ...s,
              qubitIds: s.qubitIds.filter((id) => !ids.has(id)),
            })).filter((s) => s.qubitIds.length > 0),
          });
          onEditorStateChange({ ...editorState, selectedQubitIds: new Set() });
        }
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [code, editorState, onCodeChange, onEditorStateChange]);

  // ── Render ──
  const hoveredStab = code.stabilizers.find(
    (s) => s.id === editorState.hoveredStabilizerId
  );

  const vb = `${viewBox.x} ${viewBox.y} ${viewBox.w} ${viewBox.h}`;

  return (
    <div className="relative w-full h-full overflow-hidden bg-gray-950 rounded-lg border border-gray-800">
      <svg
        ref={svgRef}
        className="w-full h-full"
        viewBox={vb}
        onMouseDown={handleSvgMouseDown}
        onMouseMove={handleSvgMouseMove}
        onMouseUp={handleSvgMouseUp}
        onMouseLeave={handleSvgMouseLeave}
        style={{ cursor: getCursor(editorState.tool, isPanning) }}
      >
        {/* Grid */}
        <defs>
          <pattern id="grid" width={GRID} height={GRID} patternUnits="userSpaceOnUse">
            <path
              d={`M ${GRID} 0 L 0 0 0 ${GRID}`}
              fill="none"
              stroke="rgba(255,255,255,0.05)"
              strokeWidth="0.5"
            />
          </pattern>
        </defs>
        <rect
          x={viewBox.x - viewBox.w}
          y={viewBox.y - viewBox.h}
          width={viewBox.w * 3}
          height={viewBox.h * 3}
          fill="url(#grid)"
        />

        {/* Stabilizer regions */}
        {code.stabilizers.map((stab) => {
          const shape = stabilizerShape(stab);
          if (!shape) return null;
          const isHovered = editorState.hoveredStabilizerId === stab.id;
          const isSelected = editorState.selectedStabilizerId === stab.id;
          const isFlipped = flippedStabilizerIds?.has(stab.id) ?? false;

          const fillColor = isFlipped ? '#f59e0b' : stab.color;
          const fillOp = isFlipped ? 0.55 : isHovered ? 0.5 : isSelected ? 0.45 : 0.3;
          const strokeColor = isFlipped ? '#fbbf24' : isSelected ? '#fff' : isHovered ? '#fff' : stab.color;
          const strokeW = isFlipped ? 3 : isSelected ? 2.5 : isHovered ? 2 : 1.5;
          const strokeOp = isFlipped ? 0.9 : isSelected ? 0.9 : isHovered ? 0.7 : 0.5;

          const shapeProps = {
            fill: fillColor,
            fillOpacity: fillOp,
            stroke: strokeColor,
            strokeWidth: strokeW,
            strokeOpacity: strokeOp,
            strokeLinejoin: 'round' as const,
            className: 'cursor-pointer',
            onMouseEnter: () => handleStabilizerMouseEnter(stab.id),
            onMouseLeave: handleStabilizerMouseLeave,
            onClick: (e: React.MouseEvent) => handleStabilizerClick(e as React.MouseEvent<SVGPolygonElement>, stab.id),
          };

          return (
            <g key={stab.id}>
              {isFlipped && (
                shape.type === 'polygon' ? (
                  <polygon points={shape.points} fill="#f59e0b" fillOpacity={0.3}
                    stroke="#fbbf24" strokeWidth={4} strokeOpacity={0.6}
                    strokeLinejoin="round" className="pointer-events-none">
                    <animate attributeName="fill-opacity" values="0.15;0.35;0.15" dur="1.2s" repeatCount="indefinite" />
                  </polygon>
                ) : (
                  <path d={shape.d} fill="#f59e0b" fillOpacity={0.3}
                    stroke="#fbbf24" strokeWidth={4} strokeOpacity={0.6}
                    strokeLinejoin="round" className="pointer-events-none">
                    <animate attributeName="fill-opacity" values="0.15;0.35;0.15" dur="1.2s" repeatCount="indefinite" />
                  </path>
                )
              )}
              {shape.type === 'polygon' ? (
                <polygon points={shape.points} {...shapeProps} />
              ) : (
                <path d={shape.d} {...shapeProps} />
              )}
            </g>
          );
        })}

        {/* Stabilizer type labels + delete buttons */}
        {code.stabilizers.map((stab) => {
          const pts = stab.qubitIds
            .map((id) => qubitById(id))
            .filter(Boolean) as Qubit[];
          if (pts.length === 0) return null;
          // For weight-2, use the lobe center; for others, use centroid
          const shape = stabilizerShape(stab);
          const c = shape && shape.type === 'path' ? shape.center : centroid(pts);
          const isHovered = editorState.hoveredStabilizerId === stab.id;
          const isFlipped = flippedStabilizerIds?.has(stab.id) ?? false;
          // Position delete button at top-right of bounding box
          const maxX = Math.max(...pts.map((p) => p.x));
          const minY = Math.min(...pts.map((p) => p.y));
          return (
            <g key={`label-${stab.id}`}>
              <text
                x={c.x}
                y={c.y - (isFlipped ? 8 : 0)}
                textAnchor="middle"
                dominantBaseline="central"
                fill={isFlipped ? '#fbbf24' : 'white'}
                fontSize="14"
                fontWeight="bold"
                className="pointer-events-none select-none"
                opacity={isFlipped ? 1 : 0.7}
              >
                {stab.type}
              </text>
              {isFlipped && (
                <text
                  x={c.x}
                  y={c.y + 10}
                  textAnchor="middle"
                  dominantBaseline="central"
                  fill="#fbbf24"
                  fontSize="10"
                  fontWeight="bold"
                  className="pointer-events-none select-none"
                >
                  FLIPPED
                </text>
              )}
              {/* Delete button on hover or selected */}
              {(isHovered || editorState.selectedStabilizerId === stab.id) && (
                <g
                  className="cursor-pointer"
                  onClick={(e) => { e.stopPropagation(); deleteStabilizer(stab.id); }}
                  onMouseEnter={() => handleStabilizerMouseEnter(stab.id)}
                >
                  <circle cx={maxX + 6} cy={minY - 6} r={8} fill="#dc2626" stroke="#fff" strokeWidth="1.5" />
                  <line x1={maxX + 3} y1={minY - 9} x2={maxX + 9} y2={minY - 3} stroke="#fff" strokeWidth="2" strokeLinecap="round" />
                  <line x1={maxX + 9} y1={minY - 9} x2={maxX + 3} y2={minY - 3} stroke="#fff" strokeWidth="2" strokeLinecap="round" />
                </g>
              )}
            </g>
          );
        })}

        {/* Edges */}
        {code.edges.map((edge) => {
          const q1 = qubitById(edge.qubitIds[0]);
          const q2 = qubitById(edge.qubitIds[1]);
          if (!q1 || !q2) return null;
          return (
            <line
              key={edge.id}
              x1={q1.x}
              y1={q1.y}
              x2={q2.x}
              y2={q2.y}
              stroke="rgba(255,255,255,0.4)"
              strokeWidth="2"
            />
          );
        })}

        {/* Edge-in-progress */}
        {editorState.tool === 'addEdge' && editorState.edgeStartQubitId && (() => {
          const q = qubitById(editorState.edgeStartQubitId);
          if (!q) return null;
          return (
            <circle
              cx={q.x}
              cy={q.y}
              r={QUBIT_R + 4}
              fill="none"
              stroke="#fbbf24"
              strokeWidth="2"
              strokeDasharray="4 2"
              className="pointer-events-none"
            />
          );
        })()}

        {/* Qubits */}
        {code.qubits.map((qubit) => {
          const isSelected = editorState.selectedQubitIds.has(qubit.id);
          const isEdgeStart = editorState.edgeStartQubitId === qubit.id;
          const isHovered = hoveredQubitId === qubit.id;
          return (
            <g
              key={qubit.id}
              onMouseDown={(e) => handleQubitMouseDown(e, qubit)}
              onMouseEnter={() => setHoveredQubitId(qubit.id)}
              onMouseLeave={() => setHoveredQubitId(null)}
            >
              {/* Selection ring */}
              {(isSelected || isEdgeStart) && (
                <circle
                  cx={qubit.x}
                  cy={qubit.y}
                  r={QUBIT_R + 4}
                  fill="none"
                  stroke={isEdgeStart ? '#fbbf24' : '#60a5fa'}
                  strokeWidth="2"
                />
              )}
              {/* Qubit circle */}
              <circle
                cx={qubit.x}
                cy={qubit.y}
                r={QUBIT_R}
                fill={isSelected ? '#1e40af' : '#1e293b'}
                stroke={isSelected ? '#60a5fa' : '#475569'}
                strokeWidth="2"
                className="cursor-pointer"
              />
              {/* Index label */}
              <text
                x={qubit.x}
                y={qubit.y}
                textAnchor="middle"
                dominantBaseline="central"
                fill="white"
                fontSize="11"
                fontWeight="600"
                className="pointer-events-none select-none"
              >
                {qubit.index}
              </text>
              {/* Delete button on hover */}
              {isHovered && !dragging && (
                <g
                  className="cursor-pointer"
                  onClick={(e) => { e.stopPropagation(); deleteQubit(qubit.id); }}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <circle cx={qubit.x + QUBIT_R} cy={qubit.y - QUBIT_R} r={7} fill="#dc2626" stroke="#fff" strokeWidth="1.5" />
                  <line x1={qubit.x + QUBIT_R - 3} y1={qubit.y - QUBIT_R - 3} x2={qubit.x + QUBIT_R + 3} y2={qubit.y - QUBIT_R + 3} stroke="#fff" strokeWidth="2" strokeLinecap="round" />
                  <line x1={qubit.x + QUBIT_R + 3} y1={qubit.y - QUBIT_R - 3} x2={qubit.x + QUBIT_R - 3} y2={qubit.y - QUBIT_R + 3} stroke="#fff" strokeWidth="2" strokeLinecap="round" />
                </g>
              )}
            </g>
          );
        })}

        {/* Marquee selection rectangle */}
        {marquee && (
          <rect
            x={Math.min(marquee.startSvg.x, marquee.endSvg.x)}
            y={Math.min(marquee.startSvg.y, marquee.endSvg.y)}
            width={Math.abs(marquee.endSvg.x - marquee.startSvg.x)}
            height={Math.abs(marquee.endSvg.y - marquee.startSvg.y)}
            fill="rgba(99,102,241,0.1)"
            stroke="rgba(99,102,241,0.6)"
            strokeWidth={1.5}
            strokeDasharray="6 3"
            className="pointer-events-none"
          />
        )}
      </svg>

      {/* Hover info panel */}
      {hoveredStab && (
        <StabilizerInfoPanel
          stabilizer={hoveredStab}
          qubits={code.qubits}
          position={panelPos}
        />
      )}

      {/* Canvas instructions overlay */}
      <div className="absolute bottom-2 left-2 text-xs text-gray-500 pointer-events-none">
        Scroll to zoom &middot; Drag to select &middot; Alt+drag to pan &middot; Delete to remove
      </div>

      {/* Minimap */}
      {code.qubits.length > 0 && (
        <Minimap
          qubits={code.qubits}
          stabilizers={code.stabilizers}
          edges={code.edges}
          viewBox={viewBox}
          onViewBoxChange={setViewBox}
        />
      )}
    </div>
  );
}

function getCursor(tool: EditorTool, isPanning: boolean): string {
  if (isPanning) return 'grabbing';
  switch (tool) {
    case 'pan':
      return 'grab';
    case 'addQubit':
      return 'crosshair';
    case 'addEdge':
      return 'crosshair';
    case 'addStabilizer':
      return 'cell';
    case 'select':
      return 'default';
    default:
      return 'default';
  }
}

// ── Minimap ──
const MINIMAP_W = 160;
const MINIMAP_H = 120;
const MINIMAP_PAD = 40;

function Minimap({
  qubits,
  stabilizers,
  edges,
  viewBox,
  onViewBoxChange,
}: {
  qubits: Qubit[];
  stabilizers: Stabilizer[];
  edges: { id: string; qubitIds: [string, string] }[];
  viewBox: { x: number; y: number; w: number; h: number };
  onViewBoxChange: (vb: { x: number; y: number; w: number; h: number }) => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [draggingMinimap, setDraggingMinimap] = useState(false);

  // Compute bounding box of all qubits
  const bounds = useMemo(() => {
    if (qubits.length === 0) return { minX: 0, minY: 0, maxX: 400, maxY: 300 };
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const q of qubits) {
      if (q.x < minX) minX = q.x;
      if (q.y < minY) minY = q.y;
      if (q.x > maxX) maxX = q.x;
      if (q.y > maxY) maxY = q.y;
    }
    // Also include the current viewport so the rect is always visible
    minX = Math.min(minX, viewBox.x);
    minY = Math.min(minY, viewBox.y);
    maxX = Math.max(maxX, viewBox.x + viewBox.w);
    maxY = Math.max(maxY, viewBox.y + viewBox.h);
    return {
      minX: minX - MINIMAP_PAD,
      minY: minY - MINIMAP_PAD,
      maxX: maxX + MINIMAP_PAD,
      maxY: maxY + MINIMAP_PAD,
    };
  }, [qubits, viewBox]);

  const worldW = bounds.maxX - bounds.minX;
  const worldH = bounds.maxY - bounds.minY;

  // Convert world coords to minimap pixel coords
  const toMiniX = (wx: number) => ((wx - bounds.minX) / worldW) * MINIMAP_W;
  const toMiniY = (wy: number) => ((wy - bounds.minY) / worldH) * MINIMAP_H;

  // Viewport rectangle on minimap
  const vpX = toMiniX(viewBox.x);
  const vpY = toMiniY(viewBox.y);
  const vpW = (viewBox.w / worldW) * MINIMAP_W;
  const vpH = (viewBox.h / worldH) * MINIMAP_H;

  const qubitMap = useMemo(() => {
    const m = new Map<string, Qubit>();
    for (const q of qubits) m.set(q.id, q);
    return m;
  }, [qubits]);

  // Click/drag on minimap to move viewport
  const moveViewport = useCallback(
    (clientX: number, clientY: number) => {
      const el = containerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const mx = clientX - rect.left;
      const my = clientY - rect.top;
      // Convert minimap pixel to world coords (center viewport there)
      const wx = bounds.minX + (mx / MINIMAP_W) * worldW - viewBox.w / 2;
      const wy = bounds.minY + (my / MINIMAP_H) * worldH - viewBox.h / 2;
      onViewBoxChange({ ...viewBox, x: wx, y: wy });
    },
    [bounds, worldW, worldH, viewBox, onViewBoxChange]
  );

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDraggingMinimap(true);
    moveViewport(e.clientX, e.clientY);
  };

  useEffect(() => {
    if (!draggingMinimap) return;
    const onMove = (e: MouseEvent) => moveViewport(e.clientX, e.clientY);
    const onUp = () => setDraggingMinimap(false);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [draggingMinimap, moveViewport]);

  return (
    <div
      ref={containerRef}
      className="absolute bottom-3 right-3 border border-gray-700 rounded bg-gray-900/90 backdrop-blur-sm overflow-hidden"
      style={{ width: MINIMAP_W, height: MINIMAP_H, cursor: 'pointer' }}
      onMouseDown={handleMouseDown}
    >
      <svg width={MINIMAP_W} height={MINIMAP_H} className="block">
        {/* Edges */}
        {edges.map((edge) => {
          const a = qubitMap.get(edge.qubitIds[0]);
          const b = qubitMap.get(edge.qubitIds[1]);
          if (!a || !b) return null;
          return (
            <line
              key={edge.id}
              x1={toMiniX(a.x)} y1={toMiniY(a.y)}
              x2={toMiniX(b.x)} y2={toMiniY(b.y)}
              stroke="rgba(255,255,255,0.15)" strokeWidth="1"
            />
          );
        })}
        {/* Stabilizer regions (simplified as dots at centroid) */}
        {stabilizers.map((stab) => {
          const pts = stab.qubitIds.map((id) => qubitMap.get(id)).filter(Boolean) as Qubit[];
          if (pts.length === 0) return null;
          const cx = pts.reduce((s, p) => s + p.x, 0) / pts.length;
          const cy = pts.reduce((s, p) => s + p.y, 0) / pts.length;
          return (
            <circle
              key={stab.id}
              cx={toMiniX(cx)} cy={toMiniY(cy)} r={2.5}
              fill={stab.type === 'X' ? 'rgba(168,85,247,0.5)' : 'rgba(59,130,246,0.5)'}
            />
          );
        })}
        {/* Qubits */}
        {qubits.map((q) => (
          <circle
            key={q.id}
            cx={toMiniX(q.x)} cy={toMiniY(q.y)} r={2}
            fill="rgba(255,255,255,0.7)"
          />
        ))}
        {/* Viewport rectangle */}
        <rect
          x={vpX} y={vpY} width={vpW} height={vpH}
          fill="rgba(99,102,241,0.15)" stroke="rgba(99,102,241,0.7)" strokeWidth="1.5"
          rx={2}
        />
      </svg>
    </div>
  );
}
